import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Close, CheckCircle, Cancel, DeleteForever } from "@mui/icons-material";
import { reviewTestimonial } from "../../../context/Testimonials";

const getDisplayName = (name) => {
  if (!name) return null;
  if (typeof name === "string") return name;
  return [name.firstName, name.middleName, name.lastName].filter(Boolean).join(" ") || null;
};

export default function TestimonialReviewDialog({ open, onClose, testimonial, onSuccess }) {
  const [action, setAction] = useState("APPROVE");
  const [reason, setReason] = useState("");
  const [publishNow, setPublishNow] = useState(false);
  const [isTopStory, setIsTopStory] = useState(false);
  const [featuredOrder, setFeaturedOrder] = useState("");
  const [rewardCredits, setRewardCredits] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setAction("APPROVE");
    setReason("");
    setPublishNow(false);
    setIsTopStory(false);
    setFeaturedOrder("");
    setRewardCredits(50);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      const body = { action, reason: reason.trim() || undefined };
      if (action === "APPROVE") {
        body.rewardCredits = Number(rewardCredits) || 0;
        if (publishNow) {
          body.publishNow = true;
          body.isTopStory = isTopStory;
          if (featuredOrder) body.featuredOrder = Number(featuredOrder);
        }
      }
      const result = await reviewTestimonial(testimonial._id, body);
      onSuccess(result, action);
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!testimonial) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={700} p={0} m={0}>
          Review Testimonial
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} p={0} m={0}>
          <strong>"{testimonial.title}"</strong> by{" "}
          {getDisplayName(testimonial.user?.name) || testimonial.user?.username}
        </Typography>

        {/* Action Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom p={0} m={0}>
            Action
          </Typography>
          <ToggleButtonGroup
            value={action}
            exclusive
            onChange={(_, val) => val && setAction(val)}
            size="small"
          >
            <ToggleButton value="APPROVE" color="success">
              <CheckCircle sx={{ mr: 0.5 }} fontSize="small" />
              Approve
            </ToggleButton>
            <ToggleButton value="REJECT" color="warning">
              <Cancel sx={{ mr: 0.5 }} fontSize="small" />
              Reject
            </ToggleButton>
            <ToggleButton value="DELETE" color="error">
              <DeleteForever sx={{ mr: 0.5 }} fontSize="small" />
              Delete
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Reason */}
        <TextField
          label="Note / Reason (optional)"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
          InputLabelProps={{ sx: { p: 0, m: 0 } }}
        />

        {/* Approval-only options */}
        {action === "APPROVE" && (
          <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, mb: 2 }}>
            <TextField
              label="Reward Credits"
              type="number"
              size="small"
              value={rewardCredits}
              onChange={(e) => setRewardCredits(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ mb: 2, width: 160 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">pts</InputAdornment>,
              }}
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={publishNow}
                    onChange={(e) => setPublishNow(e.target.checked)}
                    size="small"
                    color="info"
                  />
                }
                label="Publish immediately"
              />
            </Box>

            {publishNow && (
              <Box sx={{ pl: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTopStory}
                      onChange={(e) => setIsTopStory(e.target.checked)}
                      size="small"
                      color="warning"
                    />
                  }
                  label="Mark as Top Story"
                />
                <TextField
                  label="Featured Order"
                  type="number"
                  size="small"
                  value={featuredOrder}
                  onChange={(e) => setFeaturedOrder(e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ mt: 1, width: 160 }}
                  placeholder="e.g. 1"
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          color={action === "DELETE" ? "error" : action === "REJECT" ? "warning" : "success"}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Submitting…" : `Confirm ${action}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
